import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {useDataGridSavedState} from '../../../../../../../packages/data-grid';
import {CustomersMembersListDataGridCellKeys} from '../../../../../../../core/constants/table/data-grid-column-keys';
import {buildSharedMemberCells, buildStandardMembersColumns, renderAssignedCustomerCell, renderMemberActionsCell, renderStatusChip, resolveDataGridSaveConfig,} from './grid-shared';

/**
 * Deactivated / OnHoldCompanyLost PartnerDataGrid section.
 * Save-config is selected by the active tab name.
 */
const MembersInactiveGridSection = ({
                                        tab,
                                        members,
                                        searchData,
                                        countries,
                                        onAssign,
                                        onDetach,
                                        onDeactivate,
                                        onActivate,
                                        onDelete,
                                        canCreateMember,
                                        canOpenProfile,
                                        hasB2cWebsite,
                                        onResendActivation,
                                        getExportInfo,
                                        formLoading,
                                        membershipFallbackTitles,
                                    }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const dataGridSaveConfig = resolveDataGridSaveConfig(tab);

    const columns = useMemo(
        () => buildStandardMembersColumns(t, {includeStatus: true, pinName: false}),
        [t],
    );

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const rows = useMemo(() => members?.map((member) => ({
        key: member.id,
        data: member,
        cells: {
            ...buildSharedMemberCells(member, countries),
            [CustomersMembersListDataGridCellKeys.status]: renderStatusChip(
                member,
                t,
                membershipFallbackTitles?.[member?.memberShipName],
                hasB2cWebsite ? onResendActivation : undefined,
            ),
            [CustomersMembersListDataGridCellKeys.company]: renderAssignedCustomerCell(member, t),
            [CustomersMembersListDataGridCellKeys.actionButton]: renderMemberActionsCell({
                member,
                onAssign,
                onDetach,
                onDeactivate,
                onActivate,
                onDelete,
                onResendActivation,
                canAssign: canCreateMember,
                canOpenProfile,
                hasB2cWebsite,
            }),
        },
    })) ?? [], [
        canCreateMember,
        canOpenProfile,
        countries,
        hasB2cWebsite,
        onResendActivation,
        members,
        membershipFallbackTitles,
        onActivate,
        onAssign,
        onDeactivate,
        onDelete,
        onDetach,
        t,
    ]);

    return (
        <PartnerDataGrid
            key={dataGridSaveConfig.name}
            {...dataGridSaveConfig}
            initialLayoutState={{columns}}
            columns={dataGridColumns}
            rows={rows}
            hideFooterActions
            state={{
                sortBy: searchData.orderBy,
                pagination: searchData.paginationInfo,
                loading: {state: searchData.loading || formLoading},
                density: dataGridDensity,
            }}
            getExportInfo={getExportInfo}
            onSortByChanged={(_, current) => searchData.setOrderBy(current)}
            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prevState) => ({
                ...prevState,
                ...current,
            }))}
            onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prevState) => ({
                ...prevState,
                ...current,
            }))}
        />
    );
};

export default MembersInactiveGridSection;
